import { z } from 'zod';
import { PAYMENT_METHODS } from '../enums.js';
import {
  listFiltersQueryFragment,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import {
  businessManagerFilterSchema,
  type AttachmentRef,
  type BusinessManagerRef,
  type RecordRef,
} from './records.js';

/**
 * Payments received.
 *
 * A receipt, not a ledger. It records that a named amount arrived against one
 * invoice on one date, by one method, with whatever reference the bank or the
 * customer gave it — and the invoice's status follows from the sum of these,
 * never from somebody setting it by hand.
 *
 * The amount is positive by definition. A refund is not a negative payment: it
 * is a different document with different approval, and modelling it as a minus
 * sign here would let one careless entry make a paid invoice unpaid.
 */

const money = z.coerce.number().positive('Enter an amount greater than zero').max(1e15);

export const createPaymentSchema = z.object({
  /** Every receipt settles exactly one invoice. */
  invoiceId: uuidSchema,
  amount: money,
  /** The day the money arrived, which is rarely the day it was keyed in. */
  receivedOn: z.iso.date(),
  method: z.enum(PAYMENT_METHODS).default('BANK_TRANSFER'),
  /** Cheque number, UPI reference, bank transaction id — whatever proves it. */
  reference: optionalText(120),
  notes: optionalText(2000),
  /** Which of the organisation's own bank accounts this landed in. A system picklist. */
  depositedToAccount: z.string().trim().min(1).max(80).nullish(),
  /**
   * The real Banking-module account this receipt was deposited to, if any —
   * independent of `depositedToAccount` above, which stays for backward
   * compatibility. Posting the matching ledger entry happens server-side.
   */
  bankAccountId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
  /**
   * One key per submission of the form. A retry or double click with the same
   * key records the receipt once.
   */
  idempotencyKey: z.string().trim().min(8).max(80).optional(),
});
export type CreatePaymentInput = z.infer<typeof createPaymentSchema>;

/**
 * The invoice cannot be changed after the fact.
 *
 * Moving a receipt to a different invoice silently restates two balances. The
 * honest correction is to delete the receipt — which is audited and puts both
 * invoices back — and record it again where it belongs.
 */
export const updatePaymentSchema = toPatchSchema(createPaymentSchema.omit({ invoiceId: true, idempotencyKey: true }));
export type UpdatePaymentInput = z.infer<typeof updatePaymentSchema>;

export const PAYMENT_SORT_FIELDS = ['receivedOn', 'createdAt', 'amount'] as const;

export const listPaymentsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(PAYMENT_SORT_FIELDS).default('receivedOn'),
  businessManagerId: businessManagerFilterSchema.optional(),
  invoiceId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
  /** Inclusive, on the date the money arrived. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  ...listFiltersQueryFragment,
});
export type ListPaymentsQuery = z.infer<typeof listPaymentsQuerySchema>;

export interface PaymentSummary {
  id: string;
  /** The allocated receipt number: PAY-00007. */
  number: string;
  amount: string;
  currency: string;
  receivedOn: string;
  method: (typeof PAYMENT_METHODS)[number];
  reference: string | null;
  /** The invoice this settles, by its number. */
  invoice: RecordRef | null;
  account: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentDetail extends PaymentSummary {
  notes: string | null;
  depositedToAccount: string | null;
  /** The real Banking-module account this was deposited to, if the newer selector was used. */
  bankAccount: RecordRef | null;
  /** The legacy single-PDF slot. Its file is also in `attachments`. */
  pdfAttachment: AttachmentRef | null;
  /** Every proof of payment: any number, PDFs or photos. */
  attachments: AttachmentRef[];
  customFields: Record<string, unknown>;
  /** The state of the invoice this receipt belongs to, after it was applied. */
  invoiceTotal: string | null;
  invoiceAmountPaid: string | null;
  invoiceBalanceDue: string | null;
}
